import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import type { Operation, Rule } from "../rules/types";

import '@awesome.me/webawesome/dist/components/textarea/textarea.js';
import '@awesome.me/webawesome/dist/components/input/input.js';
import '@awesome.me/webawesome/dist/components/icon/icon.js';
import '@awesome.me/webawesome/dist/components/option/option.js';
import '@awesome.me/webawesome/dist/components/select/select.js';
import '@awesome.me/webawesome/dist/components/switch/switch.js';

@customElement("od-rule")
export class OutDesignRuleElement extends LitElement {

  @property({ type: Object })
  rule!: Rule;

  @state() public open = false;
  
  @state() private draft: Partial<Rule> = {};
  private operationText = "";

  @property({ type: Boolean, reflect: true })
  public editing = true;

  @property({ type: Boolean })
  public json = false;

  @property({ reflect: true })
  public index: number;

  @property({ type: Boolean, reflect: true })
  public activated = true;

  @property({ type: Boolean })
  public isNew = false;

  private eltTypes = [
    // Inlines
    "Emph",
    "Strong",
    "Strikeout",
    "Underline",
    "Superscript",
    "Subscript",
    "SmallCaps",
    "Quoted",
    "Cite",
    "Code",
    "Link",
    "Image",
    "Span",
    // Blocks
    "Plain",
    "Para",
    "LineBlock",
    "CodeBlock",
    "RawBlock",
    "BlockQuote",
    "OrderedList",
    "BulletList",
    "Header",
    "Figure",
    "Div",
  ]

  static styles = css`
    :host {
      display: block;
      border: 1px solid #ddd;
      padding: 0.5rem;
    }

    :host([editing]) {
      border: var(--wa-focus-ring);
    }

    :host([editing]) wa-icon[name="pen-to-square"] {
      display: none;
    }

    :host(:not([activated])) {
      opacity: 0.5;
    }

    .header {
      display: flex;
      cursor: pointer;
      font-weight: 600;
      line-height: 1.25em;
      align-items: center;
    }

    .index {
      width: 1.5em;
      text-align: right;
      padding-right: 0.5em;
      color: gray;
    }

    .title {
      flex: 1;
      text-overflow: ellipsis;
      text-overflow: ellipsis;
      overflow: hidden;
      text-wrap: nowrap;
    }

    .content {
      margin-top: 0.5rem;
      font-size: 0.85em;
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    .row {
      display: flex;
      gap: 0.5rem;
      margin-left: 2.1em;
    }

    .operator {
      display: grid; 
      grid-auto-rows: 1fr; 
      grid-template-columns: 0.7fr 2.5fr 0.3fr;
      gap: 5px;
      border-top: 1px solid lightgray;
      padding-top: 0.4em;
      margin-top: 0.4em;
    }
    
    .operator .input { grid-area: 1 / 2 / 2 / 3; }

    .operator .key {
      grid-area: 1 / 1 / 2 / 2;
      text-align: right;
      margin-right: 0.25em;
    }

    .operator .delete { 
      grid-area: 1 / 3 / 2 / 4; 
      margin: auto;
      font-size: 1.2em;
    }

    .key {
      font-weight: bold;
      color: #555;
      margin: auto 0;
    }

    wa-input, wa-textarea {
      width: 100%;
      max-width: 100%;
      min-width: 0
    }

    wa-icon {
      color: gray;
      padding: 0.4em;
      cursor: pointer;
    }

    wa-icon:hover, wa-switch::part(label):hover {
      color: black;
    }

    .textarea {
      display: block;
    }


    .operators {
      margin-top: 0.3em;
    }

    .operation-header {
      display: flex;
      align-items: baseline;
    }

    .operation-header .title {
      font-size: 1.5em;
      font-weight: bold;
    }

    .operation-header wa-switch::part(label) {
      /* font-size: 1.5em; */
      font-variant: all-small-caps;
      margin-left: 0.3em;
      font-weight: bold;
      cursor: pointer;
      color: gray;
      text-align: right;
    }

    .save {
      width: 100%;
      margin-bottom: 1rem;
      margin-top: 1em;
    }

    .selector wa-select:first-child {
      margin-bottom: 0.4em;
    }

    .missing-op {
      font-size: 1.2em;
      font-style: italic;
      margin-left: .1em;
    }
  `;

  connectedCallback() {
    super.connectedCallback()
    document.addEventListener("propagate-operator", this.handleOperator);
  }

  disconnectedCallback() {
    document.removeEventListener("propagate-operator", this.handleOperator);
    super.disconnectedCallback()
  }

  private handleOperator = (e: CustomEvent<keyof Operation>) => {
    if (!this.draft) this.draft = {};
    if (!this.draft.operation) this.draft.operation = {};

    const key = e.detail;

    if (this.draft.operation[key] !== undefined) return;

    const defaultValue = getDefaultValueOperation(key);

    this.draft = {
      ...this.draft,
      operation: {
        ...this.draft.operation,
        [key]: defaultValue
      }
    };

    this.syncOperationText();
  };

  private activateEditing(e: Event) {
    e.stopPropagation(); // prevent opening toggle

    this.editing = true;
    this.open = true;
  }

  private toggleJson() {
    this.json = !this.json;
  }

  private toggleOpening() {
    this.open = !this.open;
  }

  private toggleActivation(e: Event) {
    e.stopPropagation(); // prevent opening toggle

    const updatedRule = {
      ...this.rule,
      activated: !this.rule.activated
    };

    this.dispatchEvent(
      new CustomEvent("rule-updated", {
        detail: updatedRule,
        bubbles: true,
        composed: true
      })
    );
  }

  private delete(e: Event) {
    e.stopPropagation(); // prevent opening toggle

    this.dispatchEvent(
      new CustomEvent("rule-deleted", {
        detail: this.rule,
        bubbles: true,
        composed: true
      })
    );
  }

  private renderJsonOperation() {
    if (!this.rule?.operation) return;

    return Object.entries(this.rule.operation).map(
      ([key, value]) => html`
        <div class="row">
          <span class="key">${key}</span>
          <span>${typeof value === "object"
            ? JSON.stringify(value)
            : String(value)}</span>
        </div>
      `
    );
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has("rule") && this.rule) {
      if (this.isNew) {
        this.draft = {
          selector: "",
          operation: {}
        };
      } else {
        this.draft = {
          ...this.rule,
          operation: { ...(this.rule.operation ?? {}) }
        };
      }

      this.syncOperationText();
    }
  }

  render() {
    return html`
      <div class="header" @click=${this.toggleOpening}>
        <div class="index">${("0" + this.index).slice(-2)}.</div>
        <div class="title">${this.rule?.selector || "New rule"}</div>
        <wa-icon auto-width name="pen-to-square"
          variant="regular"
          @click=${this.activateEditing}></wa-icon>
        <wa-icon auto-width name="eye"
          variant=${ this.activated ? "" : "regular" }
          @click=${this.toggleActivation}></wa-icon>
        <wa-icon auto-width name="trash-can"
          variant="regular"
          @click=${this.delete}></wa-icon>
      </div>

      ${this.open
        ? this.editing
          ? this.renderForm()
          : html`<div class="operators">${this.renderJsonOperation()}</div>`
        : null}
    `;
  }

  private updateField(field: keyof Rule, value: unknown) {
    this.draft = { ...this.draft, [field]: value };
  }

  private save() {
    this.editing = false;

    this.dispatchEvent(
      new CustomEvent("rule-updated", {
        detail: {
          ...this.rule,
          ...this.draft,
          editing: false,
          operation: this.draft.operation ?? {}
        },
        bubbles: true,
        composed: true
      })
    );
  }

  // private save() {
  //   let parsedOperation;

  //   try {
  //     parsedOperation = JSON.parse(this.operationText || "{}");
  //   } catch (e) {
  //     alert("Invalid JSON in operation field");
  //     return;
  //   }

  //   this.draft.operation = parsedOperation

  //   this.dispatchEvent(
  //     new CustomEvent("rule-updated", {
  //       detail: {
  //         ...this.draft,
  //         editing: false
  //       },
  //       bubbles: true,
  //       composed: true
  //     })
  //   );

  //   this.editing = false;
  // }

  private renderForm() {
    return html`
      <div class="content">
        <div class="row">
          <span class="key">selector</span>
          <wa-input size="small"
            placeholder=".my-class"
            .value=${this.draft.selector || ""}
            @input=${(e: any) => this.draft.selector = e.target.value}
          />
        </div>

        <div class="operation-header">
          <span class="title">Operation</span>
          <span class="json">
            <wa-switch
              ?checked=false
              @change=${(e: any) => this.toggleJson()}
              size="small"
            >edit as JSON</wa-switch>
          </span>
        </div>
        <div>
          ${this.json
          ? html`
          <wa-textarea size="small"
            placeholder='{"classes": "cool-class"}'
            .value=${this.operationText}
            @input=${(e: any) => { this.draft.operation = JSON.parse(e.target.value); }}
          ></wa-textarea>
          `
          : this.draft.operation && (Object.keys(this.draft.operation).length > 0
            ? Object.entries(this.draft.operation).map(
                ([key, value]) => this.renderOperationField(key as keyof Operation, value)
              )
            : html`<p class="missing-op">No operator yet. Click on one below to start adding some!</p>`
          )}
        </div>
        <wa-button @click=${this.save} size="small" appearance="outlined" class="save">Save</wa-button>
      </div>
    `;
  }

  private renderOperationField(key: keyof Operation, value: any) {
    return html`
      <div class="operator">
        <div class="key">${key}</div>

        ${this.renderValueEditor(key, value)}

        <wa-icon class="delete" name="delete-left"
          @click=${() => this.removeOperator(key)}>
        </wa-icon>
      </div>
    `;
  }

  private renderValueEditor(key: keyof Operation, value: any) {
    switch(key) {

      case "type":
        return html`
          <wa-select
            value=${value}
            @change=${(e: any) => this.updateOperation(key, e.target.value)}
            size="small"
          >
            ${this.eltTypes.map(t => html`
              <wa-option value=${t}>${t}</wa-option>
            `)}
          </wa-select>
        `;

      case "level":
        return html`
          <wa-select
            value=${value}
            @change=${(e: any) => this.updateOperation(key, Number(e.target.value))}
            size="small"
          >
            ${[1,2,3,4,5,6].map(n => html`
              <wa-option size="small" value=${n}>${n}</wa-option>
            `)}
          </wa-select>
        `;

      case "classes":
        return html`
          <wa-input
            placeholder="class1 class2"
            .value=${value}
            size="small"
            @input=${(e: any) => this.updateOperation(key, e.target.value)}
          />
        `;

      case "attrs":
        return html`
          <wa-input
            placeholder='{ "key": "value" }'
            .value=${value}
            size="small"
            @input=${(e: any) => this.updateOperation(key, e.target.value)}
          />
        `;
      
      case "delete":
      case "empty":
      case "br":
      case "simplify":
      case "unwrap":
      case "id":
        return html`
          <wa-switch
            ?checked=${value}
            @change=${(e: any) => this.updateOperation(key, e.target.checked)}
            size="small"
          ></wa-switch>
        `;

      case "wrap":
        return html`
          <wa-select
            value=${value}
            @change=${(e: any) => this.updateOperation(key, e.target.value)}
            size="small"
          >
            ${this.eltTypes.map(t => html`
              <wa-option value=${t}>${t}</wa-option>
            `)}
          </wa-select>
        `;
  
      case "group":
      case "join":
        return html`
          <div class="selector">
            <wa-select
              value=${value.type}
              @change=${(e: any) =>
                this.updateNested(key, "type", e.target.value)}
              size="small"
            >
              ${this.eltTypes.map(t => html`
                <wa-option value=${t}>${t}</wa-option>
              `)}
            </wa-select>

            <wa-input
              placeholder=".class"
              value=${value.classes}
              @input=${(e: any) =>
                this.updateNested(key, "classes", e.target.value)}
              size="small"
            />
          </div>
        `;
    }
  }

  private updateOperation(key: keyof Operation, value: any) {
    this.draft = {
      ...this.draft,
      operation: {
        ...this.draft.operation,
        [key]: value
      }
    };
    this.syncOperationText();
  }

  private syncOperationText() {
    this.operationText = JSON.stringify(this.draft.operation ?? {}, null, 2);
  }

  private updateNested(key: keyof Operation, sub: string, value: any) {
    this.draft = {
      ...this.draft,
      operation: {
        ...this.draft.operation,
        [key]: {
          ...this.draft.operation?.[key],
          [sub]: value
        }
      }
    };
  }

  private removeOperator(key: keyof Operation) {
    const op = { ...this.draft.operation };
    delete op[key];

    this.draft = {
      ...this.draft,
      operation: op
    };

    this.syncOperationText();
  }
}

export function getDefaultValueOperation(key: keyof Operation) {
    switch (key) {
      case "classes": return "";
      case "type": return "Para";
      case "level": return 1;
      case "id": return true;
      case "attrs": return [];
      case "delete": return true;
      case "empty": return true;
      case "br": return true;
      case "simplify": return true;
      case "unwrap": return true;
      case "group":
        return { type: "Div" };
      case "join":
        return { type: "Header" };
      case "wrap":
        return { type: "Emph" };
      default:
        return true;
    }
  }